import { useToast } from '@get-mosh/msh-shared';
import { getHeaders } from 'src/services/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import ApiRoutes from 'src/constants/ApiRoutes';
import Routes, { moshyAuBaseUrl, moshyNzBaseUrl } from 'src/constants/Routes';
import { isVercelPreview } from 'src/env.mjs';
import { setAuthCookies } from 'src/helpers/auth';
import { ApiError } from 'src/types/Api';
import PortalRoutes from 'src/constants/PortalRoutes';
import { useTurnstile } from 'react-turnstile';
import countries, { isCountry } from 'src/constants/Countries';

export type LoginResponse = {
    uuid: string;
    accessToken: string;
    refreshToken: string;
};

type LoginRequest = {
    identity: string;
    password: string;
    captcha?: string;
};

type UseLoginProps = {
    navigate?: boolean;
};

const useLoginSuccess = (navigate = true) => {
    const queryClient = useQueryClient();
    const searchParams = useSearchParams();

    return (response: LoginResponse) => {
        setAuthCookies(response.accessToken, response.uuid);

        queryClient.invalidateQueries({
            queryKey: ['User'],
        });

        if (navigate) {
            const redirectUrl = searchParams.get('redirect_url');

            window.location.assign(
                redirectUrl
                    ? redirectUrl
                    : isVercelPreview
                      ? Routes.HOME
                      : PortalRoutes.NOTIFICATIONS,
            );
        }
    };
};

const onLoginError = (
    error: ApiError,
    showToast: ReturnType<typeof useToast>['showToast'],
) => {
    if (error.type === 'user_authentication_failure') {
        showToast({
            title: 'Incorrect email or password',
            message: 'Please check your details and try again',
            variant: 'error',
        });
    } else if (error.type === 'user_wrong_location') {
        showToast({
            title: 'Incorrect location',
            buttonLink: isCountry('AU')
                ? `${moshyNzBaseUrl}${Routes.LOGIN}`
                : `${moshyAuBaseUrl}${Routes.LOGIN}`,
            buttonText: `Go to our ${isCountry('AU') ? countries.NZ : countries.AU} login page`,
            variant: 'error',
        });
    } else {
        showToast({
            title: 'An error occurred',
            message: 'Please try again later',
            variant: 'error',
        });
    }
};

export type AppleAuthenticationRequest = {
    quizToken?: string;
    identityToken: string;
    authorizationCode: string;
    fullName?: {
        givenName: string;
        familyName: string;
    };
    source?: string;
};

export type AppleAuthenticationResponse = {
    authorization: {
        id_token: string;
        code: string;
    };
    user?: {
        email: string;
        name: {
            firstName: string;
            lastName: string;
        };
    };
};

async function postAppleLogin(
    request: AppleAuthenticationRequest,
): Promise<LoginResponse> {
    const res = await fetch(ApiRoutes.APPLE_LOGIN, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });
    const data = await res.json();

    if (!res.ok) {
        throw data as ApiError;
    }

    return data as LoginResponse;
}

export const useLoginWithApple = ({ navigate = true }: UseLoginProps = {}) => {
    const loginSuccess = useLoginSuccess(navigate);
    const { showToast } = useToast();

    return useMutation<LoginResponse, ApiError, AppleAuthenticationRequest>({
        mutationKey: ['AppleLogin'],
        mutationFn: (request: AppleAuthenticationRequest) =>
            postAppleLogin(request),
        onSuccess: loginSuccess,
        onError: (error) => {
            onLoginError(error, showToast);
        },
    });
};

type LoginWithGoogleRequest = {
    quizToken: string | undefined;
    idToken: string;
    source?: string;
};

async function postGoogleLogin(
    request: LoginWithGoogleRequest,
): Promise<LoginResponse> {
    const res = await fetch(ApiRoutes.GOOGLE_LOGIN, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });
    const data = await res.json();

    if (!res.ok) {
        throw data as ApiError;
    }

    return data as LoginResponse;
}

export const useLoginWithGoogle = ({ navigate = true }: UseLoginProps = {}) => {
    const loginSuccess = useLoginSuccess(navigate);
    const { showToast } = useToast();

    return useMutation<LoginResponse, ApiError, LoginWithGoogleRequest>({
        mutationKey: ['GoogleLogin'],
        mutationFn: (request: LoginWithGoogleRequest) =>
            postGoogleLogin(request),
        onSuccess: loginSuccess,
        onError: (error) => {
            onLoginError(error, showToast);
        },
    });
};

async function postLogin(request: LoginRequest): Promise<LoginResponse> {
    const res = await fetch(ApiRoutes.LOGIN, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
    });
    const data = await res.json();

    if (!res.ok) {
        throw data as ApiError;
    }
    return data as LoginResponse;
}

export const useLogin = ({ navigate = true }: UseLoginProps = {}) => {
    const turnstile = useTurnstile();
    const loginSuccess = useLoginSuccess(navigate);
    const { showToast } = useToast();

    return useMutation<LoginResponse, ApiError, LoginRequest>({
        mutationKey: ['Login'],
        mutationFn: (request: LoginRequest) => postLogin(request),
        onError: (error) => {
            onLoginError(error, showToast);
            turnstile.reset();
        },
        onSuccess: loginSuccess,
    });
};
